/**
 * 파일명 : usr0202m.tsx
 * 설명 : 근속현황
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.0       2023-04-04     윤성수    최초작성
 * 1.1       2024-03-12     기라성    엔터키로 조회 
 * 1.2       2024-03-19     김시현    직원명 콤보박스 추가 및 목록 화면에 꽉차도록 수정    
 */

/************************************************
 * Import 영역  
 ************************************************/
/*  React  */
import React from 'react';
import locale from 'antd/lib/locale/ko_KR';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Usr0202mAction } from '../../../redux/slice/usr/usr0202m_slice';
import { IInquiryCndsState, IUserItemState, IUserListItemState } from '../../../redux/models/usr/usr0202m_model';

/*  AntD  */
import { Button, Space, Select, Row, Col, Layout, Typography, Collapse, Input, DatePicker, ConfigProvider, Table } from 'antd';
import { SearchOutlined, CaretRightOutlined } from '@ant-design/icons';

/*  업무  */
import ExportExcelFile from 'components/exportExcelFile';
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';
import * as commonDate from 'libs/commonDate';
import dayjs from 'dayjs';

/************************************************
 * 전역변수 영역
 ************************************************/

const Usr0202m = () => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
    const dispatch = useAppDispatch();
    const userList     = useAppSelector((state) => state.Usr0202m.userList);
    const inquiryCnds  = useAppSelector((state) => state.Usr0202m.inquiryCnds);
    const userDatas    = useAppSelector((state) => state.Usr0202m.userDatas);
    const current      = useAppSelector((state) => state.Usr0202m.current);
    const pageSize     = useAppSelector((state) => state.Usr0202m.pageSize);

    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
    React.useEffect(() => {
        fn_User()
        fn_Search();
    }, []);


    /* Render 변수 선언영역 */
    /* AntD */
    const { Content } = Layout;
    const { Text }    = Typography;
    const { Panel }   = Collapse;

    /* Table Columns */
    const columns = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>번호</div>,
                dataIndex: 'no',
                key: 'no',
                align: 'center',
                width: '5%',
                render: (_: any, __: IUserListItemState, index: number) => (current - 1) * pageSize + index + 1,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>직원명</div>,
                dataIndex: 'userNm',
                key: 'userNm',
                align: 'left',
                width: '20%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>직위</div>,
                dataIndex: 'postNm',
                key: 'postNm',
                align: 'center',
                width: '20%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>부서</div>,
                dataIndex: 'deptNm',
                key: 'deptNm',
                align: 'left',
                width: '20%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>실입사일</div>,
                dataIndex: 'realEntDt',
                key: 'realEntDt',
                align: 'center',
                width: '20%',
                render: (text:string) => commonDate.maskDate(text, 'YYYY-MM-DD'),
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>근속기간</div>,
                dataIndex: 'wrkPerid',
                key: 'wrkPerid',
                align: 'center',
                width: '20%',
            }];
            return arr;
    }, [current, pageSize]);


    /* 엑셀다운로드 */
    const excelParams = {
        datas: userDatas,
        btnName: '엑셀파일 내려받기',
        fileName: '근속현황_' + inquiryCnds.baseDt,
        sheetName:'근속현황_' + inquiryCnds.baseDt,
        columns: columns(),
    }

    /**
     * 기능 : 초기화 이벤트 선언
     * param : 
     */
    const fn_Reset = () => {
        dispatch(Usr0202mAction.initState());
    };

    /**
     * 기능 : 결과초기화 이벤트 선언
     * param : 
     */
    const fn_ResetResult = () => {
        dispatch(Usr0202mAction.setUserDatas([]));
    };

    /**
     *  기능 : RangePicker 데이터 변경
     *  설명 : RangePicker 데이터 변경값을 받는다
     *  param :
     *  return : None
     */
    const calendar_onChange = (_:any, dateStrings:any) => {
        dispatch(Usr0202mAction.setInquiryCnds({ ...inquiryCnds, baseDt : dateStrings }));
    };
    
    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = (e:any) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case 'btn_Search': {
                fn_ResetResult();   // 결과초기화
                fn_Search();        // 조회
                break;
            }

            default:
        }
    };

    /**
     * 기능 : fn_User
     * 설명 : 직원명 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
     const fn_User = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            userStCd: 'A'
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRR0003',
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                responseData.userList.sort((a:IUserItemState, b:IUserItemState) => (a.userNm > b.userNm) ? 1 : -1)
                dispatch(Usr0202mAction.setUserList(responseData.userList));
            },
        });
    };

    const userOptions = React.useCallback(() => {
        let arrUserOptions: IUserItemState[] = [];

        if (common.isAdmin()) {
            arrUserOptions.push({label: '전체', value: ''});
        }

        userList.forEach((item:IUserItemState) => {
            arrUserOptions.push({
                label: item.userNm,
                value: item.empNo
            });
        });

        return arrUserOptions;
    }, [userList]);


    /**
     * 기능 : 검색조회
     * param : 
     */
    const fn_Search = () => {
        dispatch(Usr0202mAction.setCurrent(1));

        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData:IInquiryCndsState = {
            ...inquiryCnds
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRR0008',
            success: (responseData:any) => {
                dispatch(Usr0202mAction.setUserDatas(responseData.userWrkPerids));
                dispatch({ type: 'SET_STATE', data_node: 'dataList', value: responseData.userWrkPerids });
            },
        });
    };


    /**
     * 기능 : 입력 후 Enter key 입력 시 조회
     * 설명 : 입력컴포넌트
     * param : 
     * return : None
     */
    const handleOnKeyUp = (e:any) => {
        if (e.key === 'Enter') {
            fn_Search(); // 조회
        }
    };

    return (
        <>
            <Layout>
                <Content>
                    <Collapse   
                        bordered={false}
                        defaultActiveKey={['1']}
                        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                        className='site-collapse-custom-collapse'
                    >
                        <Panel header='검색조건' key='1' className='site-collapse-custom-panel'>
                            <Row
                                align='middle'
                                justify='space-between'
                                style={{ marginTop: 10 }}
                            >
                                <Col flex='9.5'>
                                    <Space size='large'>
                                        <Space size='small'>
                                            <label>기준일자</label>
                                            <ConfigProvider locale={locale}>
                                                <DatePicker
                                                    style={{ width: 120 }}
                                                    id='tx_calendar'
                                                    allowClear={false}
                                                    value={dayjs(inquiryCnds.baseDt)}
                                                    onChange={calendar_onChange}
                                                    onKeyUp ={handleOnKeyUp}
                                                />
                                            </ConfigProvider>
                                        </Space>
                                        <Space size='small'>
                                            <label>근속기간(개월)</label>
                                            <Input id='inputWrkPerid' name='wrkPerid' autoComplete='off' value={inquiryCnds.wrkPerid} style={{width: 100}} type='number' onKeyUp ={handleOnKeyUp} onChange={(e:any) => {
                                                dispatch(Usr0202mAction.setInquiryCnds({ ...inquiryCnds, wrkPerid : e.currentTarget.value }));
                                            }}/>
                                        </Space>
                                        <Space size='small'>
                                            <label>직원명</label>
                                            <Select 
                                                showSearch
                                                style={{
                                                    width: 200,
                                                }}
                                                optionFilterProp='label'
                                                defaultValue=''
                                                filterSort={(optionA: any, optionB: any) => {
                                                    const labelA = (optionA?.label ?? '').toLowerCase();
                                                    const labelB = (optionB?.label ?? '').toLowerCase();
                                                    
                                                    if (labelA === '전체') return -1;
                                                    if (labelB === '전체') return 1;
                                                    
                                                    return labelA.localeCompare(labelB);
                                                }}
                                                options={userOptions()}
                                                onKeyUp={handleOnKeyUp}
                                                value={inquiryCnds.empNo}
                                                onChange={val => dispatch(Usr0202mAction.setInquiryCnds({...inquiryCnds, empNo: val}))}
                                            ></Select>
                                        </Space>
                                    </Space>
                                </Col>
                                <Col flex='0.5'>
                                    <Space>
                                        <Button
                                            name='btn_Reset'
                                            className='background-color-default-button'
                                            onClick={fn_Reset}
                                        >
                                            초기화
                                        </Button>
                                        <Button
                                            name='btn_Search'
                                            className='background-color-primary-button'
                                            icon={<SearchOutlined />}
                                            onClick={onClick}
                                        >
                                            조회
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Panel>
                    </Collapse>
                    <Row align='middle' style={{ height: 50 }}>
                        <Col flex='9.5'>
                            <Space>
                                <img className='title-icon' alt='' />
                                <Text strong>근속현황</Text>
                            </Space>
                        </Col>
                        <Col flex='0.5'>
                            <Space>
                                <ExportExcelFile excelParams={excelParams}/>
                            </Space>
                        </Col>
                    </Row>
                    <Table
                        dataSource={userDatas}
                        key='empNo'
                        columns={columns()}
                        size='small'
                        style={{width:'100%'}}
                        scroll={{y:550}}
                        pagination={{
                            pageSize: pageSize,
                            current: current,
                            showTotal : () => '총 ' + userDatas.length + ' 건',
                            onChange: (page, pageSize) => {
                                dispatch(Usr0202mAction.setCurrent(page));
                                dispatch(Usr0202mAction.setPageSize(pageSize));
                            },
                        }}
                    />
                </Content>
            </Layout>
        </>
    );
};

export default Usr0202m;

